/* eslint-disable */
import { useEffect, useRef, useState } from 'react';
import { ClickAwayListener, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Popper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { CircleCrossIcon, LocalPhoneIcon, OnlineIcon, PaymentIcon, RenameIcon } from 'assets';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import { PatientListDialogType, ToggleDotMenuButton, Transitions } from 'components';
import { useAppFeatureFlags, useTeleconsent } from 'hooks';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { AcceptPaymentStatusEnum, ApiUser } from 'types';
import { getIsAdminByRoles } from 'utils';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

import styles from './DotMenu.module.scss';

type DotMenuProps = {
  hasBadState?: boolean;
  onDialogOpen: (type: PatientListDialogType) => void;
};

export function DotMenu({ hasBadState, onDialogOpen }: DotMenuProps) {
  const { t, i18n } = useTranslation('providerRoom');
  const isRtl = i18n.dir() === 'rtl';
  const { participantRenameFeature } = useAppFeatureFlags();
  const user: ApiUser | null = useAppSelector(selectUser);
  const { isOpen: isTeleconsentOpen } = useTeleconsent();
  const isAdmin = getIsAdminByRoles(user?.clinicUser.roles ?? []);
  const canUseTeleconsent =
    !!user?.clinicUser.teleconsentEnabled &&
    (isAdmin || !!user?.clinicUser.teleconsentActivateForAllStaff) &&
    !isTeleconsentOpen;
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const prevOpen = useRef(open);

  useEffect(() => {
    if (prevOpen.current && !open) {
      anchorRef.current && anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDialogOpen = (type: PatientListDialogType): void => {
    onDialogOpen(type);
    setOpen(false);
  };

  return (
    <>
      <ToggleDotMenuButton className={styles.menuIcon} onClick={handleToggle} ref={anchorRef} />

      <Popper
        placement={isRtl ? 'left-start' : 'right-start'}
        className={classnames(styles.dotMenu, { [styles.dotMenuRtl]: isRtl })}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 14],
              },
            },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <Transitions in={open} {...TransitionProps}>
            <ClickAwayListener onClickAway={handleClose}>
              <List
                disablePadding
                sx={{
                  backgroundColor: isDarkMode ? '#2A2F35' : '#FFF',
                  color: isDarkMode ? '#FFFFFF' : '#393534',
                  border: isDarkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.08)',
                  boxShadow: isDarkMode ? '0 8px 24px rgba(0,0,0,0.35)' : '0 2px 8px rgb(0 0 0 / 5%)',
                  '& .MuiListItemButton-root': {
                    direction: isRtl ? 'rtl' : 'ltr',
                  },
                  '& .MuiListItemIcon-root': {
                    minWidth: 'unset',
                    ...(isRtl
                      ? {
                          marginLeft: '10px',
                          marginRight: 0,
                        }
                      : {
                          marginRight: '10px',
                          marginLeft: 0,
                        }),
                  },
                  '& .MuiListItemText-primary': {
                    color: isDarkMode ? '#FFFFFF' : '#393534',
                    whiteSpace: 'nowrap',
                    textAlign: isRtl ? 'right' : 'left',
                  },
                  '& .MuiListItemButton-root:hover': {
                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                  },
                }}
              >
                <ListItem>
                  <ListItemButton
                    role={undefined}
                    onClick={() => handleDialogOpen(PatientListDialogType.TRANSFER)}
                    dense
                  >
                    <ListItemIcon>
                      <SwapHorizIcon color='primary' />
                    </ListItemIcon>
                    <ListItemText primary={t('transfer')} />
                  </ListItemButton>
                </ListItem>

                <ListItem>
                  <ListItemButton role={undefined} onClick={() => handleDialogOpen(PatientListDialogType.STATUS)} dense>
                    <ListItemIcon>
                      <OnlineIcon sx={{ color: hasBadState ? '#FF3D3D' : '#3EB157' }} />
                    </ListItemIcon>
                    <ListItemText primary={t('dialogs.statusTitle')} />
                  </ListItemButton>
                </ListItem>

                {participantRenameFeature && (
                  <ListItem>
                    <ListItemButton
                      role={undefined}
                      onClick={() => handleDialogOpen(PatientListDialogType.RENAME)}
                      dense
                    >
                      <ListItemIcon>
                        <RenameIcon color='primary' />
                      </ListItemIcon>
                      <ListItemText primary={t('rename')} />
                    </ListItemButton>
                  </ListItem>
                )}

                <ListItem>
                  <ListItemButton
                    role={undefined}
                    onClick={() => handleDialogOpen(PatientListDialogType.CONTACT)}
                    dense
                  >
                    <ListItemIcon>
                      <LocalPhoneIcon color='primary' />
                    </ListItemIcon>
                    <ListItemText primary={t('dialogs.contactTitle')} />
                  </ListItemButton>
                </ListItem>

                {user?.acceptPaymentStatus === AcceptPaymentStatusEnum.Connected &&
                  user?.clinicUser.paymentsAllowed && (
                    <ListItem>
                      <ListItemButton
                        role={undefined}
                        onClick={() => handleDialogOpen(PatientListDialogType.PAYMENT)}
                        dense
                      >
                        <ListItemIcon>
                          <PaymentIcon color='primary' />
                        </ListItemIcon>
                        <ListItemText primary={t('payment')} />
                      </ListItemButton>
                    </ListItem>
                  )}

                {canUseTeleconsent && (
                  <ListItem>
                    <ListItemButton
                      role={undefined}
                      onClick={() => handleDialogOpen(PatientListDialogType.TELECONSENT)}
                      dense
                    >
                      <ListItemIcon>
                        <AssignmentOutlinedIcon color='primary' />
                      </ListItemIcon>
                      <ListItemText primary={t('teleconsent')} />
                    </ListItemButton>
                  </ListItem>
                )}

                <ListItem>
                  <ListItemButton role={undefined} onClick={() => handleDialogOpen(PatientListDialogType.REMOVE)} dense>
                    <ListItemIcon>
                      <CircleCrossIcon color='primary' />
                    </ListItemIcon>
                    <ListItemText primary={t('remove')} />
                  </ListItemButton>
                </ListItem>
              </List>
            </ClickAwayListener>
          </Transitions>
        )}
      </Popper>
    </>
  );
}
// online 3EB157
// offline FF3D3D
